import { Link } from "react-router-dom";
import { Workflow as WorkflowIcon, Play } from "lucide-react";
import { StatusPill } from "@/components/common/StatusPill";
import { ShapeIcons, FavouriteStar } from "@/components/catalogue/Bits";
import { EntityAvatar } from "@/components/catalogue/EntityAvatar";
import { useCatalogue } from "@/context/CatalogueContext";
import { agentName, shapesOf, statusOf } from "@/lib/catalogue";
import { teamColour } from "@/lib/constants";
import { cn } from "@/lib/utils";

export const EntryCard = ({ entity, kind }) => {
  const { teamsById } = useCatalogue();
  const isFlow = kind === "workflow";
  const id = isFlow ? entity.id : entity.emp_no;
  const name = isFlow ? entity.name : agentName(entity);
  const path = isFlow ? `/workflows/${id}` : `/agents/${id}`;
  const team = teamsById[entity.team];
  const colour = teamColour(team, team?._index);
  const live = !!entity.live;
  const blurb = isFlow ? entity.key_benefit || entity.one_line : entity.function;
  const sub = isFlow ? entity.one_line && entity.key_benefit ? entity.one_line : null : entity.key_benefit;
  const tid = `${isFlow ? "workflow" : "agent"}-card`;
  return (
    <article className="entry-card card-elevated relative flex flex-col overflow-hidden" style={{ "--team": colour }} data-testid={`${tid}-${id}`} data-live={live}>
      <span className={cn("h-[3px] w-full", !live && "grayscale")} style={{ background: colour, filter: live ? undefined : "grayscale(1)" }} aria-hidden="true" />
      <div className="flex flex-1 flex-col p-5">
        <div className={cn("flex flex-1 flex-col", !live && "opacity-[0.55]")}>
          <div className="flex items-start gap-3">
            <EntityAvatar entity={entity} name={name} className={cn(!live && "grayscale")} />
            <div className="min-w-0 flex-1">
              <h3 className="flex items-center gap-1.5 truncate text-base font-semibold">
                {isFlow && <WorkflowIcon className="!h-4 !w-4 shrink-0 text-muted-foreground" aria-label="Workflow" />}
                <Link to={path} className="truncate after:absolute after:inset-0 hover:underline" data-testid={`${tid}-link-${id}`}>{name}</Link>
              </h3>
              <p className="truncate text-sm text-muted-foreground">{isFlow ? "Workflow" : entity.kind || "Agent"} · {team?.section || team?.name || "No team"}</p>
            </div>
          </div>
          {blurb && <p className="mt-3 line-clamp-2 text-[15px]" title={blurb}>{blurb}</p>}
          {sub && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{sub}</p>}
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 pt-4">
          <div className={cn("flex items-center gap-3", !live && "opacity-[0.55]")}>
            {isFlow ? (entity.human_gates?.length > 0 && <span className="text-sm text-muted-foreground">{entity.human_gates.length} approval gates</span>) : <ShapeIcons shapes={shapesOf(entity)} />}
          </div>
          <div className="relative z-10 flex items-center gap-1.5">
            {live && (
              <Link to={`${path}?tab=run`} className="run-quick inline-flex h-8 items-center gap-1 rounded-full bg-action px-3 text-sm font-medium text-action-foreground focus-visible:opacity-100" data-testid={`${tid}-run-${id}`} aria-label={`Run ${name}`}>
                <Play className="!h-3.5 !w-3.5" aria-hidden="true" /> Run
              </Link>
            )}
            <StatusPill status={live ? statusOf(entity) : "soon"} testid={`${tid}-status-${id}`} />
            <FavouriteStar targetId={id} name={name} />
          </div>
        </div>
      </div>
    </article>
  );
};

export const AgentCard = ({ agent }) => <EntryCard entity={agent} kind="agent" />;
export const WorkflowCard = ({ workflow }) => <EntryCard entity={workflow} kind="workflow" />;
